import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Aziende from "@/pages/Aziende";
import Assistente from "@/pages/Assistente";
import Dashboard from "@/pages/Dashboard";
import Preventivi from "@/pages/Preventivi";
import Fatture from "@/pages/Fatture";
import Spese from "@/pages/Spese";
import Contatti from "@/pages/Contatti";
import Listino from "@/pages/Listino";
import Preziario from "@/pages/Preziario";
import Agenda from "@/pages/Agenda";
import Ddt from "@/pages/Ddt";
import Posta from "@/pages/Posta";
import Cantieri from "@/pages/Cantieri";
import WhatsApp from "@/pages/WhatsApp";
import AttivitaOggi from "@/pages/AttivitaOggi";
import Incassi from "@/pages/Incassi";
import Impostazioni from "@/pages/Impostazioni";
import { Legal } from "@/pages/Legal";
import PagamentoEsito from "@/pages/PagamentoEsito";
import Abbonamento from "@/pages/Abbonamento";
import PasswordDimenticata from "@/pages/PasswordDimenticata";
import CookieConsent from "@/components/CookieConsent";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { isSimple, SIMPLE_PATHS } from "@/lib/edition";
import { ServerWakingBanner } from "@/components/AppBanners";

const Protected = ({ children }) => {
  const { user, ready, serverDown, refresh } = useAuth();
  const location = useLocation();
  if (!ready)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 bg-background text-muted-foreground text-center px-6" data-testid="app-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <div>Caricamento…</div>
        <ServerWakingBanner inline />
      </div>
    );
  if (!user && serverDown)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-center px-6" data-testid="server-down-screen">
        <div className="font-heading text-lg font-bold">Server non raggiungibile</div>
        <p className="text-sm text-muted-foreground max-w-sm">Controlla la connessione internet e riprova. Sei ancora collegato al tuo account.</p>
        <Button onClick={refresh} data-testid="server-down-retry-button">Riprova</Button>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // I collaboratori Junior accedono solo all'Agenda (i propri sopralluoghi).
  if (user.role === "junior" && location.pathname !== "/agenda")
    return <Navigate to="/agenda" replace />;
  if (isSimple(user) && !SIMPLE_PATHS.includes(location.pathname)) return <Navigate to="/" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user && !user.is_superadmin) return <Navigate to="/" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/preventivi" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/registrati" element={<Register />} />
            <Route path="/password-dimenticata" element={<PasswordDimenticata />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/termini" element={<Legal doc="termini" />} />
            <Route path="/cookie" element={<Legal doc="cookie" />} />
            <Route path="/payment/success" element={<PagamentoEsito />} />
            <Route path="/payment/cancel" element={<PagamentoEsito cancelled />} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/preventivi" element={<Preventivi />} />
              <Route path="/fatture" element={<AdminRoute><Fatture /></AdminRoute>} />
              <Route path="/ddt" element={<Ddt />} />
              <Route path="/posta" element={<AdminRoute><Posta /></AdminRoute>} />
              <Route path="/cantieri" element={<AdminRoute><Cantieri /></AdminRoute>} />
              <Route path="/whatsapp" element={<AdminRoute><WhatsApp /></AdminRoute>} />
              <Route path="/attivita-oggi" element={<AdminRoute><AttivitaOggi /></AdminRoute>} />
              <Route path="/pagamenti-incassi" element={<AdminRoute><Incassi /></AdminRoute>} />
              <Route path="/spese" element={<AdminRoute><Spese /></AdminRoute>} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/listino" element={<Listino />} />
              <Route path="/preziario" element={<Preziario />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/impostazioni" element={<AdminRoute><Impostazioni /></AdminRoute>} />
              <Route path="/aziende" element={<SuperAdminRoute><Aziende /></SuperAdminRoute>} />
              <Route path="/assistente" element={<AdminRoute><Assistente /></AdminRoute>} />
              <Route path="/abbonamento" element={<AdminRoute><Abbonamento /></AdminRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
